import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, GitCompare, Share2, HelpCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import ShareModal from "../components/results/ShareModal";
import CountryCard from "../components/results/CountryCard";
import BundleCheckoutSection from "../components/results/BundleCheckoutSection";
import AskQuestionPanel from "../components/results/AskQuestionPanel";
import GuideContents from "../components/results/GuideContents";

const ResultsPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasingCountry, setPurchasingCountry] = useState(null);

  const [showShareModal, setShowShareModal] = useState(false);
  const [showAskQuestion, setShowAskQuestion] = useState(false);
  const [question, setQuestion] = useState("");
  const [askingQuestion, setAskingQuestion] = useState(false);
  const [aiResponse, setAiResponse] = useState("");

  const [selectedForBundle, setSelectedForBundle] = useState([]);
  const [bundleCheckoutLoading, setBundleCheckoutLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe");

  const [bookmarkedCountries, setBookmarkedCountries] = useState(new Set());
  const [walletCreditDollars, setWalletCreditDollars] = useState(0);

  // Load bookmarks + wallet credit
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/bookmarks`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setBookmarkedCountries(new Set(res.data.map((b) => b.country_name))))
      .catch(() => {});
    axios
      .get(`${API}/referrals/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setWalletCreditDollars(res.data.wallet_credit_dollars || 0))
      .catch(() => {});
  }, [token]);

  // Load recommendations (sessionStorage first, then API)
  useEffect(() => {
    const load = async () => {
      const stored = sessionStorage.getItem("expatmatch_recommendations");
      if (stored) {
        setRecommendations(JSON.parse(stored));
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/recommendations/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.length > 0) {
          setRecommendations(response.data[0].recommendations);
        }
      } catch {
        toast.error("Failed to load recommendations");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const toggleBookmark = async (rec) => {
    const country = rec.country_name;
    const isBookmarked = bookmarkedCountries.has(country);
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${encodeURIComponent(country)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBookmarkedCountries((prev) => {
          const next = new Set(prev);
          next.delete(country);
          return next;
        });
        toast.success(`Removed ${country} from saved`);
      } else {
        await axios.post(
          `${API}/bookmarks`,
          {
            country_name: country,
            recommendation: {
              match_score: rec.match_score,
              summary: rec.summary,
              image_url: rec.image_url,
            },
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBookmarkedCountries((prev) => {
          const next = new Set(prev);
          next.add(country);
          return next;
        });
        toast.success(`Saved ${country}`);
      }
    } catch {
      toast.error("Could not update bookmark");
    }
  };

  const toggleBundleSelection = (countryName) => {
    setSelectedForBundle((prev) => {
      if (prev.includes(countryName)) return prev.filter((c) => c !== countryName);
      if (prev.length >= 3) {
        toast.error("Maximum 3 countries per bundle");
        return prev;
      }
      return [...prev, countryName];
    });
  };

  const handleBundleCheckout = async () => {
    if (selectedForBundle.length === 0) {
      toast.error("Please select at least one country");
      return;
    }
    setBundleCheckoutLoading(true);
    try {
      const response = await axios.post(
        `${API}/payments/create-bundle-checkout`,
        {
          country_names: selectedForBundle,
          origin_url: window.location.origin,
          payment_method: paymentMethod,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create checkout";
      toast.error(message);
    } finally {
      setBundleCheckoutLoading(false);
    }
  };

  const handlePurchaseReport = async (countryName) => {
    setPurchasingCountry(countryName);
    try {
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        {
          country_name: countryName,
          origin_url: window.location.origin,
          payment_method: "stripe",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create checkout";
      toast.error(message);
      setPurchasingCountry(null);
    }
  };

  const handleAskQuestion = async () => {
    if (!question.trim()) return;
    setAskingQuestion(true);
    setAiResponse("");
    try {
      const countriesList = recommendations.map((r) => r.country_name).join(", ");
      const response = await axios.post(`${API}/support/chat`, {
        message: `Based on my country recommendations (${countriesList}), I have a question: ${question}`,
        session_id: `results_${user?.id || "guest"}`,
        attempt_count: 1,
      });
      setAiResponse(response.data.response);
    } catch {
      toast.error("Failed to get response. Please try again.");
    } finally {
      setAskingQuestion(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your recommendations...</p>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-primary mb-4">No recommendations yet</h2>
          <p className="text-muted-foreground mb-6">
            Complete the questionnaire to get personalized country recommendations.
          </p>
          <Button
            onClick={() => navigate("/questionnaire")}
            className="btn-animated bg-primary"
            data-testid="start-questionnaire-btn"
          >
            Start Questionnaire
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/questionnaire")}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Retake questionnaire
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1
                className="text-3xl sm:text-4xl font-bold text-primary mb-2"
                data-testid="results-title"
              >
                Your Top Matches
              </h1>
              <p className="text-muted-foreground">
                Based on your profile, here are the countries where you'd thrive
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowAskQuestion(!showAskQuestion)}
                variant="outline"
                className="gap-2"
                data-testid="ask-question-btn"
              >
                <HelpCircle className="h-4 w-4" />
                Ask a Question
              </Button>
              <Button
                onClick={() => setShowShareModal(true)}
                variant="outline"
                className="gap-2"
                data-testid="share-results-btn"
              >
                <Share2 className="h-4 w-4" />
                Share Results
              </Button>
              <Button
                onClick={() => navigate("/compare")}
                variant="outline"
                className="gap-2"
                data-testid="compare-countries-btn"
              >
                <GitCompare className="h-4 w-4" />
                Compare
              </Button>
            </div>
          </div>
        </div>

        {/* Ask a Question Panel */}
        {showAskQuestion && (
          <AskQuestionPanel
            question={question}
            setQuestion={setQuestion}
            onAsk={handleAskQuestion}
            loading={askingQuestion}
            aiResponse={aiResponse}
          />
        )}

        {/* Results Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {recommendations.map((rec, index) => (
            <CountryCard
              key={rec.country_name}
              rec={rec}
              index={index}
              isBookmarked={bookmarkedCountries.has(rec.country_name)}
              onToggleBookmark={toggleBookmark}
              isInBundle={selectedForBundle.includes(rec.country_name)}
              onToggleBundle={toggleBundleSelection}
              onPurchase={handlePurchaseReport}
              purchasing={purchasingCountry === rec.country_name}
            />
          ))}
        </div>

        {/* Bundle Pricing Section */}
        <BundleCheckoutSection
          selectedForBundle={selectedForBundle}
          walletCreditDollars={walletCreditDollars}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          onCheckout={handleBundleCheckout}
          loading={bundleCheckoutLoading}
        />

        {/* What's in each guide */}
        <GuideContents />

        {/* Dashboard Link */}
        <div className="mt-8 text-center">
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            data-testid="go-to-dashboard-btn"
          >
            View Dashboard
          </Button>
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        recommendations={recommendations}
      />
    </div>
  );
};

export default ResultsPage;
